import type { InvitedEntryDecision } from "@verity-connect/contracts";

/**
 * Asking a door what an address holds, and opening it with a password.
 *
 * Written once for every invited door rather than per screen. The staff door grew its own
 * version of this and the others did not grow one at all, which is how three of them ended
 * up able to post a code and nothing else.
 */
export type InvitedDoor = "organizer" | "presenter" | "school" | "exhibitor";

export async function resolveInvitedDoor(
  door: InvitedDoor,
  email: string,
  humanCheck: string,
): Promise<InvitedEntryDecision> {
  const response = await fetch(`/api/${door}/entry`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, "cf-turnstile-response": humanCheck }),
  });
  const body = await response.json().catch(() => null) as { message?: string } | null;
  if (!response.ok) throw new Error(body?.message ?? "That address could not be checked.");
  return body as unknown as InvitedEntryDecision;
}

export async function openInvitedDoor(door: InvitedDoor, email: string, password: string): Promise<void> {
  const response = await fetch(`/api/${door}/join`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { message?: string } | null;
    throw new Error(body?.message ?? "That did not sign you in.");
  }
  // Deliberately does not navigate. The caller offers the device sign-in first, because a
  // password and a passkey are both worth having and the screen should not make somebody
  // choose one by walking away the moment the other is set.
}
